import { useCallback, useEffect, useState } from "react";

import {
  CADENCE_SAMPLE_LIMIT,
  type GlucoseReading,
  type NewGlucoseReading,
} from "@heraion/shared";

import { apiFetch } from "./api";

// Web glucose quick-entry resource (2026-07-01): the recent readings (for the
// card's "current" line) + a create that POSTs a canonical mg/dL to
// /api/me/glucose. Online-only — no offline outbox, mirroring useMetabolicWeb's
// read-only posture (the outbox stays on mobile). The shared QuickGlucoseCard
// owns the unit conversion, so create receives canonical mg/dL. apiFetch stamps
// the Idempotency-Key header on every non-GET, so no explicit key is needed.
//
// HM-1 (2026-07-15) — the TWIN of apps/mobile/src/measurements/useGlucose.ts:
// same request, same bug, and they feed the same card on four surfaces (mobile
// Home + Settings→Measurements, web Home + Measurements). Fix them together or
// they diverge.
//
// This asked for `?limit=1` and rendered it as «Latest: N» with no date, so it
// showed the most recent reading that EVER existed, at any age, forever — the
// CGM died 10-07 and this line would still have read "Latest: 118" in
// September. It now takes a small window instead: the card can only tell a live
// feed from a dead one by learning the feed's OWN cadence (a CGM breathes every
// 5 minutes, a hand-typed reading every few days), and one reading has no
// cadence. CADENCE_SAMPLE_LIMIT is that evidence, not a display list — the card shows
// items[0] and reads the rest only as rhythm.


export interface UseGlucoseWeb {
  /** Newest-first. The card renders [0] and uses the tail to learn cadence. */
  readings: GlucoseReading[];
  create: (mgDl: number) => Promise<boolean>;
}

export function useGlucoseWeb(): UseGlucoseWeb {
  const [readings, setReadings] = useState<GlucoseReading[]>([]);
  const [seq, setSeq] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch<{ items: GlucoseReading[] }>(
          `/api/me/glucose?limit=${CADENCE_SAMPLE_LIMIT}`,
        );
        if (!cancelled) setReadings(res.items);
      } catch {
        // offline / none yet — leave as-is; the card shows its placeholder.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [seq]);

  const create = useCallback(async (mgDl: number): Promise<boolean> => {
    const body: NewGlucoseReading = {
      takenAt: new Date().toISOString(),
      mgDl,
    };
    try {
      await apiFetch("/api/me/glucose", { method: "POST", body });
      // Refetch rather than splice the optimistic value in: the server owns id
      // and takenAt, and a hand-made row here would be a display snapshot that
      // can drift from the real one.
      setSeq((s) => s + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  return { readings, create };
}
